// DraggableResizableLegend.jsx
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// CSS for larger slider thumb - injected once
const sliderStyles = `
  .legend-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }
  .legend-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }
  .legend-slider::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 4px;
    background: #e5e7eb;
  }
  .legend-slider::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: #e5e7eb;
  }
`;

/**
 * A fixed-position legend wrapper with a slider for size control.
 * Fixed in bottom-left corner with a slider above to adjust size.
 */
const DraggableResizableLegend = ({ children, initialScale = 1 }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(initialScale);
  const [showControls, setShowControls] = useState(false);

  // Inject slider styles once on mount
  useEffect(() => {
    const styleId = 'legend-slider-styles';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = sliderStyles;
      document.head.appendChild(styleEl);
    }
  }, []);

  // Min and max scale values
  const minScale = 0.6;
  const maxScale = 1.8;

  return (
    <div
      ref={containerRef}
      id="custom-legend-container"
      className="absolute z-10 flex flex-col items-start"
      style={{
        left: 16,
        bottom: 16,
      }}
      data-legend-left={16}
      data-legend-bottom={16}
      data-legend-scale={scale}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Invisible hover extension zone - extends above the legend to make slider accessible */}
      <div
        className="absolute pointer-events-auto"
        style={{
          left: -10,
          right: -10,
          bottom: -10,
          top: -60, // Extends 60px above to reach the slider
        }}
      />

      {/* Scalable legend wrapper */}
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'bottom left',
          '--legend-scale': scale,
        }}
      >
        <div className="legend-content">
          {children}
        </div>
      </div>

      {/* Size slider control - positioned above with higher z-index */}
      <div
        className={`absolute flex items-center gap-2 px-3 py-2 bg-white rounded-md border border-gray-300 shadow-md transition-opacity duration-200 z-20 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          bottom: '100%',
          left: 0,
          marginBottom: 8,
        }}
      >
        <span className="text-xs text-gray-500">S</span>
        <input
          type="range"
          min={minScale * 100}
          max={maxScale * 100}
          value={scale * 100}
          onChange={(e) => setScale(parseInt(e.target.value) / 100)}
          className="legend-slider w-28 cursor-pointer"
          title={`Scale: ${Math.round(scale * 100)}%`}
        />
        <span className="text-xs text-gray-500">L</span>
      </div>
    </div>
  );
};

DraggableResizableLegend.propTypes = {
  children: PropTypes.node.isRequired,
  initialScale: PropTypes.number,
};

export default DraggableResizableLegend;
